/**
 * The Ryerson Gives Back teaser on the Ryerson Commitment landing page
 * (`src/pages/why-ryerson/ryerson-commitment/index.tsx`, its only importer): title, paragraph,
 * image and a "More about" link to the full Ryerson Gives Back page.
 *
 * It has no layout of its own. It renders the Certification & Compliance teaser layouts,
 * `../CertificationAndCompliance/CertificateAndComplianceDesktop.tsx` from `lg` up and
 * `../CertificationAndCompliance/CertificateAndComplianceMobile.tsx` below, with the copy in
 * `./Content.tsx`. A change to those layouts changes both teasers.
 *
 * The full page is `src/pages/why-ryerson/ryerson-commitment/ryerson-gives-back/index.tsx`, built
 * from the sections in `../RyersonGivesBackPage/`.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { RyersonGivesBackContentDesktop, RyersonGivesBackContentMobile } from "./Content";
import CertificateAndComplianceMobile from "../CertificationAndCompliance/CertificateAndComplianceMobile";
import CertificateAndComplianceDesktop from "../CertificationAndCompliance/CertificateAndComplianceDesktop";
import { useApplication } from "@ryerson/frontend.application";

/** Theme background choice for the teaser. */
export type backgroundType = "primary" | "secondary";

/**
 * One language's teaser copy. Same shape as `CertificateAndComplianceProps` in
 * `../CertificationAndCompliance/CertificateAndCompliance.tsx`, which the layouts take.
 */
export interface RyersonGivesBackProps {
	/**
	 * Desktop: `primary` makes the right half of the split background white, anything else light
	 * gray. Mobile: the `ContentSection` type, defaulting to `primary`.
	 */
	background?: backgroundType;
	/** Heading; slugified (lower-cased, whitespace runs to `-`) it is the teaser's anchor id. */
	title: string;
	/** The teaser paragraph. */
	content: string;
	imageUrl: string;
	/** Label beside the round arrow button; `\n` breaks the line. */
	buttonLabel: string;
	/** Target of the arrow button and its label. */
	moreAboutLink: string;
}

/** Renders the teaser in the visitor's language. Takes no props. */
const RyersonGivesBack: React.FC = () => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<>
			<Media lessThan="lg">
				<CertificateAndComplianceMobile {...RyersonGivesBackContentMobile[language]} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<CertificateAndComplianceDesktop {...RyersonGivesBackContentDesktop[language]} />
			</Media>
		</>
	);
};

export default RyersonGivesBack;
